import { DateTime } from 'luxon';
import { RouteLocationRaw } from 'vue-router';

import { loadLuxonDateTime } from '@rygen/rygen-ui/util';
import type { Order } from '@rygen/tms-search-model/search/order';
import type { OrderShippingPoint } from '@rygen/tms-search-model/search/orderShippingPoint';
import type { Shipment } from '@rygen/tms-search-model/search/shipment';
import type { ShipmentStop } from '@rygen/tms-search-model/search/shipmentStop';

export type QuickSearchHighlight = Record<string, string[]>;

export type OrderQuickSearchResult = Order;
export type ShipmentQuickSearchResult = Shipment;

export type QuickSearchImportantDate = {
  dateTime: DateTime;
  type: QuickSearchImportantDateType;
};

export type QuickSearchImportantDateType =
  | 'REQUESTED_APPOINTMENT_EARLIEST'
  | 'REQUESTED_APPOINTMENT_LATEST'
  | 'SCHEDULED_APPOINTMENT_EARLIEST'
  | 'SCHEDULED_APPOINTMENT_LATEST'
  | 'EXPECTED_ARRIVAL'
  | 'ACTUAL_ARRIVAL'
  | 'ACTUAL_DEPARTURE';

export function getQuickSearchShippingPointMostImportantDate(
  stop: ShipmentStop | OrderShippingPoint | undefined,
): QuickSearchImportantDate | undefined {
  if (!stop) {
    return;
  }

  const tz = stop.location?.timeZone;

  // This is possible because for the purposes of this method, ShipmentStop is a
  // superset of OrderShippingPoint.
  // The extra dates that are present on the ShipmentStop will be undefined &
  // ignored while evaluating a OrderShippingPoint
  const unifiedStop = stop as ShipmentStop;

  const datesWithTypes: {
    type: QuickSearchImportantDateType;
    date: string | undefined;
  }[] = [
    { type: 'ACTUAL_DEPARTURE', date: unifiedStop.actualDeparture },
    { type: 'ACTUAL_ARRIVAL', date: unifiedStop.actualArrival },
    { type: 'EXPECTED_ARRIVAL', date: unifiedStop.expectedArrival },
    {
      type: 'SCHEDULED_APPOINTMENT_EARLIEST',
      date: unifiedStop.scheduledAppointmentRange?.start,
    },
    {
      type: 'SCHEDULED_APPOINTMENT_LATEST',
      date: unifiedStop.scheduledAppointmentRange?.end,
    },
    {
      type: 'REQUESTED_APPOINTMENT_EARLIEST',
      date: unifiedStop.requestedAppointmentRange?.start,
    },
    {
      type: 'REQUESTED_APPOINTMENT_LATEST',
      date: unifiedStop.requestedAppointmentRange?.end,
    },
  ];

  for (const dateWithType of datesWithTypes) {
    if (dateWithType.date) {
      return {
        dateTime: loadLuxonDateTime(dateWithType.date, tz)!,
        type: dateWithType.type,
      };
    }
  }
}

abstract class QuickQuickSearchResponseItem<
  T extends OrderQuickSearchResult | ShipmentQuickSearchResult,
> {
  readonly source: T;
  readonly highlight: QuickSearchHighlight;
  readonly score?: number;
  readonly rank?: number;
  readonly type;
  readonly searchType = 'QuickQuickSearch';

  protected constructor(
    type: 'ORDER' | 'SHIPMENT',
    opts: Partial<QuickQuickSearchResponseItem<T>>,
  ) {
    if (!opts.source) {
      throw new Error('source is required');
    }

    this.source = opts.source;
    this.highlight = { ...opts.highlight };
    this.score = opts.score;
    this.rank = opts.rank;
    this.type = type;
  }

  public get origMostImportantDate(): QuickSearchImportantDate | undefined {
    return getQuickSearchShippingPointMostImportantDate(this.source.origin);
  }

  public get destMostImportantDate(): QuickSearchImportantDate | undefined {
    return getQuickSearchShippingPointMostImportantDate(
      this.source.destination,
    );
  }

  public abstract get route(): RouteLocationRaw;
}

export class QuickQuickSearchResponseItemOrder extends QuickQuickSearchResponseItem<OrderQuickSearchResult> {
  constructor(opts: Partial<QuickQuickSearchResponseItemOrder>) {
    super('ORDER', opts);
  }

  public get route() {
    return {
      name: 'orderDetail',
      params: { orderId: this.source.id },
    };
  }
}

export class QuickQuickSearchResponseItemShipment extends QuickQuickSearchResponseItem<ShipmentQuickSearchResult> {
  constructor(opts: Partial<QuickQuickSearchResponseItemOrder>) {
    super('SHIPMENT', opts);
  }

  public get route() {
    return {
      name: 'shipmentDetail',
      params: { orderId: this.source.id },
    };
  }
}

export class QuickQuickSearchResponse {
  orderSearchResponse: QuickQuickSearchResponseItemOrder[];
  shipmentSearchResponse: QuickQuickSearchResponseItemShipment[];

  constructor(opts?: QuickQuickSearchResponse) {
    this.orderSearchResponse =
      opts?.orderSearchResponse?.map(
        (val) => new QuickQuickSearchResponseItemOrder(val),
      ) ?? [];
    this.shipmentSearchResponse =
      opts?.shipmentSearchResponse?.map(
        (val) => new QuickQuickSearchResponseItemShipment(val),
      ) ?? [];
  }
}
